/**
 * @fileoverview Component for displaying Paddle account status
 * @module paddle-connect/components/PaddleAccountStatus
 *
 * Shows both sandbox and production connection status.
 * Environment selection happens at PaywallProduct level, not here.
 */

import { useState, useCallback } from 'react';
import { PaddleWebhookHealthStatus, PaddleWebhookDiagnostic } from '@modules/paddle-connect/components/PaddleWebhookHealthStatus';
import { PaddleWebhookSecretDialog } from '@modules/paddle-connect/components/PaddleWebhookSecretDialog';
import { PaddleConnectionError } from '@modules/paddle-connect/components/PaddleConnectionError';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  Stack,
  Tooltip,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Payments,
  Webhook,
  MoreVert,
  Delete,
  Science,
  Rocket,
} from '@mui/icons-material';
import type { PaddleAccountStatusProps, PaddleEnvironmentConfig } from '@modules/paddle-connect/types';
import { usePaddleConnect } from '@modules/paddle-connect/hooks/usePaddleConnect';
import { PADDLE_CONNECT_TEST_IDS } from '@modules/paddle-connect/testIds';
import { Add } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';

const actionTextColor = (theme: Theme) => theme.palette.mode === 'dark'
  ? theme.palette.primary.main : theme.palette.primary.dark;

/**
 * Display Paddle account connection status
 *
 * Shows both sandbox and production environments:
 * - Each environment shows connected/not connected status
 * - Webhook secret status per environment
 * - Disconnect per environment or all
 *
 * Note: Environment selection for products happens at PaywallProduct level,
 * not globally here.
 *
 * @example
 * ```tsx
 * <PaddleAccountStatus
 *   projectId="abc123"
 *   showDisconnect
 *   onDisconnect={() => refetch()}
 * />
 * ```
 */
export function PaddleAccountStatus({
  projectId,
  showDisconnect = true,
  onDisconnect,
  onConnectEnvironment,
}: PaddleAccountStatusProps) {
  const { status, loading, error, refresh, disconnect, updateWebhookSecret } = usePaddleConnect(projectId);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [editingSecret, setEditingSecret] = useState<'sandbox' | 'production' | null>(null);
  const [secretSaved, setSecretSaved] = useState(false);

  // Get environment configs
  const sandboxConfig = status?.sandbox as PaddleEnvironmentConfig | undefined;
  const productionConfig = status?.production as PaddleEnvironmentConfig | undefined;

  // Determine which environments are connected
  const sandboxConnected = sandboxConfig?.connected ?? false;
  const productionConnected = productionConfig?.connected ?? false;
  const bothConnected = sandboxConnected && productionConnected;

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleDisconnect = useCallback(
    async (environment: 'sandbox' | 'production' | 'all') => {
      const message = environment === 'all'
        ? 'Are you sure you want to disconnect Paddle? This will remove ALL credentials.'
        : `Are you sure you want to disconnect Paddle ${environment}?`;

      if (window.confirm(message)) {
        try {
          await disconnect(environment);
          onDisconnect?.();
        } catch { /* The hook exposes the save/readback error with a read-only retry. */ }
      }
      handleMenuClose();
    },
    [disconnect, onDisconnect, handleMenuClose]
  );

  if (loading && !status) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">Loading Paddle status...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!status) return <PaddleConnectionError error={error} hasVerifiedStatus={false} onRetry={refresh} />;

  if (!status.connected) {
    return (
      <Card>
        <CardContent>
          {error && <PaddleConnectionError error={error} hasVerifiedStatus onRetry={refresh} />}
          <Stack direction="row" spacing={2} alignItems="center">
            <Cancel color="error" />
            <Typography>Paddle not connected</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {error && <PaddleConnectionError error={error} hasVerifiedStatus onRetry={refresh} />}
        {secretSaved && <Alert severity="success" sx={{ mb: 2 }}>Webhook secret saved. Send a test event from Paddle to check delivery.</Alert>}
        {editingSecret && <PaddleWebhookSecretDialog key={editingSecret} environment={editingSecret}
          onSave={secret => updateWebhookSecret(editingSecret, secret)}
          onClose={() => setEditingSecret(null)}
          onSaved={() => { setEditingSecret(null); setSecretSaved(true); }} />}
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Payments color="primary" />
          <Typography variant="h6">Paddle Billing</Typography>
          <Chip
            icon={<CheckCircle />}
            label="Connected"
            color="success"
            size="small"
          />
          <Button onClick={refresh} disabled={loading} data-testid={PADDLE_CONNECT_TEST_IDS.refreshHealth}
            sx={{ ml: { sm: 'auto' }, color: actionTextColor }}>{loading ? 'Checking…' : 'Check again'}</Button>
        </Stack>
        <PaddleWebhookDiagnostic diagnostic={status.webhookDiagnostic} />

        <Divider sx={{ my: 2 }} />

        {/* Environment Status */}
        <Stack spacing={2}>
          {/* Sandbox Environment */}
          <Box
            data-testid={PADDLE_CONNECT_TEST_IDS.status('sandbox')}
            data-connected={sandboxConnected ? 'true' : 'false'}
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: sandboxConnected ? 'action.hover' : 'transparent',
              border: '1px solid',
              borderColor: sandboxConnected ? 'warning.main' : 'divider',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Science fontSize="small" color={sandboxConnected ? 'warning' : 'disabled'} />
                <Typography fontWeight={500}>Sandbox</Typography>
                {sandboxConnected ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Connected"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    label="Not Connected"
                    size="small"
                    variant="outlined"
                    sx={{ color: 'text.secondary' }}
                  />
                )}
              </Stack>
              {sandboxConnected && sandboxConfig?.hasWebhookSecret && (
                <Tooltip title="Signing secret saved; this does not confirm webhook delivery">
                  <Webhook fontSize="small" color="success" />
                </Tooltip>
              )}
              {sandboxConnected && !sandboxConfig?.hasWebhookSecret && (
                <Tooltip title="Signing secret missing in Segmently. Use Add webhook secret below.">
                  <Warning fontSize="small" color="warning" />
                </Tooltip>
              )}
              {!sandboxConnected && onConnectEnvironment && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  data-testid={PADDLE_CONNECT_TEST_IDS.connectEnvButton('sandbox')}
                  onClick={() => onConnectEnvironment('sandbox')}
                >
                  Connect
                </Button>
              )}
            </Stack>
            {sandboxConnected && (
              <Button size="small" sx={{ mt: 1, color: actionTextColor }} onClick={() => { setSecretSaved(false); setEditingSecret('sandbox'); }}
                data-testid={PADDLE_CONNECT_TEST_IDS.editSecret('sandbox')}>
                {sandboxConfig?.hasWebhookSecret ? 'Update webhook secret' : 'Add webhook secret'}
              </Button>
            )}
            {sandboxConnected && sandboxConfig?.connectedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                Connected {new Date(sandboxConfig.connectedAt).toLocaleDateString()}
                {sandboxConfig.sellerId && ` • Seller: ${sandboxConfig.sellerId}`}
              </Typography>
            )}
            {sandboxConnected && <PaddleWebhookHealthStatus environment="sandbox" config={sandboxConfig}
              loading={loading} refreshFailed={!!error} />}
          </Box>

          {/* Production Environment */}
          <Box
            data-testid={PADDLE_CONNECT_TEST_IDS.status('production')}
            data-connected={productionConnected ? 'true' : 'false'}
            sx={{
              p: 1.5,
              borderRadius: 1,
              backgroundColor: productionConnected ? 'action.hover' : 'transparent',
              border: '1px solid',
              borderColor: productionConnected ? 'success.main' : 'divider',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Rocket fontSize="small" color={productionConnected ? 'success' : 'disabled'} />
                <Typography fontWeight={500}>Production</Typography>
                {productionConnected ? (
                  <Chip
                    icon={<CheckCircle />}
                    label="Connected"
                    color="success"
                    size="small"
                    variant="outlined"
                    sx={{ color: (theme) => theme.palette.mode === 'dark'
                      ? theme.palette.success.light : theme.palette.success.dark }}
                  />
                ) : (
                  <Chip
                    label="Not Connected"
                    size="small"
                    variant="outlined"
                    sx={{ color: 'text.secondary' }}
                  />
                )}
              </Stack>
              {productionConnected && productionConfig?.hasWebhookSecret && (
                <Tooltip title="Signing secret saved; this does not confirm webhook delivery">
                  <Webhook fontSize="small" color="success" />
                </Tooltip>
              )}
              {productionConnected && !productionConfig?.hasWebhookSecret && (
                <Tooltip title="Signing secret missing in Segmently. Use Add webhook secret below.">
                  <Warning fontSize="small" color="warning" />
                </Tooltip>
              )}
              {!productionConnected && onConnectEnvironment && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<Add />}
                  data-testid={PADDLE_CONNECT_TEST_IDS.connectEnvButton('production')}
                  onClick={() => onConnectEnvironment('production')}
                >
                  Connect
                </Button>
              )}
            </Stack>
            {productionConnected && (
              <Button size="small" sx={{ mt: 1, color: actionTextColor }} onClick={() => { setSecretSaved(false); setEditingSecret('production'); }}
                data-testid={PADDLE_CONNECT_TEST_IDS.editSecret('production')}>
                {productionConfig?.hasWebhookSecret ? 'Update webhook secret' : 'Add webhook secret'}
              </Button>
            )}
            {productionConnected && productionConfig?.connectedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                Connected {new Date(productionConfig.connectedAt).toLocaleDateString()}
                {productionConfig.sellerId && ` • Seller: ${productionConfig.sellerId}`}
              </Typography>
            )}
            {productionConnected && <PaddleWebhookHealthStatus environment="production" config={productionConfig}
              loading={loading} refreshFailed={!!error} />}
          </Box>

          {/* Hint about environment usage */}
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            💡 Environment is selected when creating a Paywall Product, not globally.
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Test without a purchase: Paddle → Developer tools → Simulations. Refresh this card after sending a test event.
        </Typography>

        {/* Actions */}
        {showDisconnect && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                data-testid={PADDLE_CONNECT_TEST_IDS.disconnectMenu}
                onClick={handleMenuOpen}
                endIcon={<MoreVert />}
              >
                Disconnect
              </Button>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                {sandboxConnected && (
                  <MenuItem data-testid={PADDLE_CONNECT_TEST_IDS.disconnect('sandbox')} onClick={() => handleDisconnect('sandbox')}>
                    <ListItemIcon>
                      <Delete fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Disconnect Sandbox</ListItemText>
                  </MenuItem>
                )}
                {productionConnected && (
                  <MenuItem data-testid={PADDLE_CONNECT_TEST_IDS.disconnect('production')} onClick={() => handleDisconnect('production')}>
                    <ListItemIcon>
                      <Delete fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Disconnect Production</ListItemText>
                  </MenuItem>
                )}
                {bothConnected && <Divider />}
                <MenuItem data-testid={PADDLE_CONNECT_TEST_IDS.disconnect('all')} onClick={() => handleDisconnect('all')}>
                  <ListItemIcon>
                    <Delete fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Disconnect All</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default PaddleAccountStatus;
